/**
 * Invoice Settings Dialog Component
 * Extracted from Invoices.js for better maintainability
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Settings, Upload, Loader2, X, Plus, RefreshCw, Check, Image as ImageIcon } from 'lucide-react';
import { PDF_THEMES, fixImageUrl } from '../../utils/invoicePdfGenerator';

const DEFAULT_UNITS = ['nos', 'hrs', 'days', 'sqft', 'kg', 'liters', 'pcs', 'set'];

const InvoiceSettingsDialog = ({
  open,
  onClose,
  invoiceSettings,
  setInvoiceSettings,
  settingsLoading,
  uploadingField,
  onSave,
  onImageSelect,
  onAddCustomUnit,
  onRemoveCustomUnit,
  newCustomUnit,
  setNewCustomUnit
}) => {
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Invoice Settings
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="bank" className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 h-auto">
            <TabsTrigger value="bank" className="text-xs sm:text-sm">Bank</TabsTrigger>
            <TabsTrigger value="company" className="text-xs sm:text-sm">Company</TabsTrigger>
            <TabsTrigger value="defaults" className="text-xs sm:text-sm">Defaults</TabsTrigger>
            <TabsTrigger value="signature" className="text-xs sm:text-sm">Signature</TabsTrigger>
            <TabsTrigger value="pdftemplate" className="text-xs sm:text-sm col-span-3 sm:col-span-1">PDF Style</TabsTrigger>
          </TabsList>
          
          {/* Bank Details Tab */}
          <TabsContent value="bank" className="space-y-4 mt-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              Bank details will be auto-filled when creating new invoices.
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={invoiceSettings.bankDetails?.bank_name || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    bankDetails: { ...prev.bankDetails, bank_name: e.target.value }
                  }))}
                  placeholder="State Bank of India"
                />
              </div>
              <div>
                <Label>Account Holder Name</Label>
                <Input
                  value={invoiceSettings.bankDetails?.account_name || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    bankDetails: { ...prev.bankDetails, account_name: e.target.value }
                  }))}
                  placeholder="Your Business Name"
                />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input
                  value={invoiceSettings.bankDetails?.account_number || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    bankDetails: { ...prev.bankDetails, account_number: e.target.value }
                  }))}
                  placeholder="1234567890"
                />
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input
                  value={invoiceSettings.bankDetails?.ifsc_code || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    bankDetails: { ...prev.bankDetails, ifsc_code: e.target.value }
                  }))}
                  placeholder="SBIN0001234"
                />
              </div>
              <div>
                <Label>UPI ID</Label>
                <Input
                  value={invoiceSettings.bankDetails?.upi_id || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    bankDetails: { ...prev.bankDetails, upi_id: e.target.value }
                  }))}
                  placeholder="yourname@upi"
                />
              </div>
            </div>
            
            {/* Payment QR Code */}
            <div className="border-t pt-4">
              <Label>Payment QR Code</Label>
              <p className="text-xs text-gray-500 mb-2">Upload your UPI/Payment QR code image</p>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageSelect(e, 'qr')}
                      className="hidden"
                      disabled={uploadingField === 'qr'}
                    />
                    {uploadingField === 'qr' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                        <span className="text-sm">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Click to upload QR Code</span>
                      </>
                    )}
                  </label>
                </div>
                {invoiceSettings.paymentQrCodeUrl && (
                  <div className="relative">
                    <img 
                      src={fixImageUrl(invoiceSettings.paymentQrCodeUrl)} 
                      alt="Payment QR Code" 
                      className="w-24 h-24 object-contain border rounded"
                    />
                    <button
                      onClick={() => setInvoiceSettings(prev => ({ ...prev, paymentQrCodeUrl: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Company Details Tab */}
          <TabsContent value="company" className="space-y-4 mt-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              Company details appear on the invoice header.
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company/Business Name</Label>
                <Input
                  value={invoiceSettings.companyDetails?.name || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, name: e.target.value }
                  }))}
                  placeholder="Your Venue Name"
                />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input
                  value={invoiceSettings.companyDetails?.gstin || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, gstin: e.target.value }
                  }))}
                  placeholder="29ABCDE1234F1Z5"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={invoiceSettings.companyDetails?.phone || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, phone: e.target.value }
                  }))}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={invoiceSettings.companyDetails?.email || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, email: e.target.value }
                  }))}
                  placeholder="contact@yourvenue.com"
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={invoiceSettings.companyDetails?.address || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, address: e.target.value }
                  }))}
                  placeholder="123, Main Street, City - 560001"
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <Label>Company Logo</Label>
                <div className="flex items-start gap-4 mt-2">
                  <div className="flex-1">
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onImageSelect(e, 'logo')}
                        className="hidden"
                        disabled={uploadingField === 'logo'}
                      />
                      {uploadingField === 'logo' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                          <span className="text-sm">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Click to upload Logo</span>
                        </>
                      )}
                    </label>
                  </div>
                  {invoiceSettings.companyDetails?.logo_url && (
                    <div className="relative">
                      <img 
                        src={fixImageUrl(invoiceSettings.companyDetails.logo_url)} 
                        alt="Company Logo" 
                        className="h-16 object-contain border rounded"
                      />
                      <button
                        onClick={() => setInvoiceSettings(prev => ({ 
                          ...prev, 
                          companyDetails: { ...prev.companyDetails, logo_url: '' }
                        }))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Defaults Tab */}
          <TabsContent value="defaults" className="space-y-4 mt-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              These values will be pre-filled when creating new invoices.
            </div>
            
            <div>
              <Label>Invoice Number Prefix</Label>
              <Input
                value={invoiceSettings.invoicePrefix || 'INV'}
                onChange={(e) => setInvoiceSettings(prev => ({
                  ...prev,
                  invoicePrefix: e.target.value
                }))}
                placeholder="INV"
                className="w-32"
              />
              <p className="text-xs text-gray-500 mt-1">e.g., INV-202601-0001</p>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Default GST Settings</Label>
                <Switch
                  checked={invoiceSettings.gstDefaults?.enabled || false}
                  onCheckedChange={(checked) => setInvoiceSettings(prev => ({
                    ...prev,
                    gstDefaults: { ...prev.gstDefaults, enabled: checked }
                  }))}
                />
              </div>
              
              {invoiceSettings.gstDefaults?.enabled && (
                <div className="space-y-3 pl-4 border-l-2 border-teal-500">
                  <div>
                    <Label>GST Type</Label>
                    <Select
                      value={invoiceSettings.gstDefaults?.gst_type || 'cgst_sgst'}
                      onValueChange={(v) => setInvoiceSettings(prev => ({
                        ...prev,
                        gstDefaults: { ...prev.gstDefaults, gst_type: v }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cgst_sgst">CGST + SGST (Same State)</SelectItem>
                        <SelectItem value="igst">IGST (Different State)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {invoiceSettings.gstDefaults?.gst_type === 'igst' ? (
                    <div>
                      <Label>IGST Rate (%)</Label>
                      <Input
                        type="number"
                        value={invoiceSettings.gstDefaults?.igst_rate || 18}
                        onChange={(e) => setInvoiceSettings(prev => ({
                          ...prev,
                          gstDefaults: { ...prev.gstDefaults, igst_rate: parseFloat(e.target.value) || 18 }
                        }))}
                        className="w-24"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>CGST Rate (%)</Label>
                        <Input
                          type="number"
                          value={invoiceSettings.gstDefaults?.cgst_rate || 9}
                          onChange={(e) => setInvoiceSettings(prev => ({
                            ...prev,
                            gstDefaults: { ...prev.gstDefaults, cgst_rate: parseFloat(e.target.value) || 9 }
                          }))}
                        />
                      </div>
                      <div>
                        <Label>SGST Rate (%)</Label>
                        <Input
                          type="number"
                          value={invoiceSettings.gstDefaults?.sgst_rate || 9}
                          onChange={(e) => setInvoiceSettings(prev => ({
                            ...prev,
                            gstDefaults: { ...prev.gstDefaults, sgst_rate: parseFloat(e.target.value) || 9 }
                          }))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Custom Units Section */}
            <div className="border-t pt-4">
              <Label>Custom Units</Label>
              <p className="text-xs text-gray-500 mb-2">Add custom measurement units for line items (e.g., plates, rooms, persons)</p>
              
              <div className="flex gap-2 mb-3">
                <Input
                  value={newCustomUnit}
                  onChange={(e) => setNewCustomUnit(e.target.value)}
                  placeholder="Enter unit name"
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && onAddCustomUnit()}
                />
                <Button type="button" onClick={onAddCustomUnit} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              
              {/* Default Units */}
              <div className="mb-2">
                <span className="text-xs text-gray-500">Default Units:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {DEFAULT_UNITS.map(unit => (
                    <Badge key={unit} variant="secondary" className="text-xs">
                      {unit}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Custom Units */}
              {invoiceSettings.customUnits?.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Custom Units:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {invoiceSettings.customUnits.map(unit => (
                      <Badge key={unit} variant="default" className="text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                        {unit}
                        <button
                          onClick={() => onRemoveCustomUnit(unit)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t pt-4">
              <Label>Default Terms & Conditions</Label>
              <Textarea
                value={invoiceSettings.defaultTermsConditions || ''}
                onChange={(e) => setInvoiceSettings(prev => ({
                  ...prev,
                  defaultTermsConditions: e.target.value
                }))}
                placeholder="Payment due within 15 days. Cancellation charges apply..."
                rows={4}
              />
            </div>
            
            <div>
              <Label>Default Notes</Label>
              <Textarea
                value={invoiceSettings.defaultNotes || ''}
                onChange={(e) => setInvoiceSettings(prev => ({
                  ...prev,
                  defaultNotes: e.target.value
                }))}
                placeholder="Thank you for your business!"
                rows={2}
              />
            </div>
          </TabsContent>
          
          {/* Signature Tab */}
          <TabsContent value="signature" className="space-y-4 mt-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              Add authorized signature details that appear at the bottom of invoices.
            </div>

            {/* Show Signature Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 dark:bg-gray-800/50">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-foreground">Show Signature on Invoice</p>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">Display signature section at the bottom of PDF invoices</p>
              </div>
              <Switch
                data-testid="toggle-show-signature"
                checked={invoiceSettings.signature?.showOnInvoice !== false}
                onCheckedChange={(checked) => setInvoiceSettings(prev => ({
                  ...prev,
                  signature: { ...prev.signature, showOnInvoice: checked }
                }))}
              />
            </div>
            
            <div>
              <Label>Signature Image</Label>
              <div className="flex items-start gap-4 mt-2">
                <div className="flex-1">
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageSelect(e, 'signature')}
                      className="hidden"
                      disabled={uploadingField === 'signature'}
                    />
                    {uploadingField === 'signature' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                        <span className="text-sm">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Click to upload Signature</span>
                      </>
                    )}
                  </label>
                </div>
                {invoiceSettings.signature?.url && (
                  <div className="relative">
                    <img 
                      src={fixImageUrl(invoiceSettings.signature.url)} 
                      alt="Signature" 
                      className="h-16 object-contain border rounded"
                    />
                    <button
                      onClick={() => setInvoiceSettings(prev => ({ 
                        ...prev, 
                        signature: { ...prev.signature, url: '' }
                      }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Signatory Name</Label>
                <Input
                  value={invoiceSettings.signature?.name || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    signature: { ...prev.signature, name: e.target.value }
                  }))}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  value={invoiceSettings.signature?.designation || ''}
                  onChange={(e) => setInvoiceSettings(prev => ({
                    ...prev,
                    signature: { ...prev.signature, designation: e.target.value }
                  }))}
                  placeholder="Manager / Authorized Signatory"
                />
              </div>
            </div>
          </TabsContent>

          {/* PDF Template Tab */}
          <TabsContent value="pdftemplate" className="space-y-6 mt-4">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-sm text-purple-700 dark:text-purple-300">
              Customize the look and feel of your downloaded PDF invoices.
            </div>

            {/* Color Theme Selection */}
            <div>
              <Label className="text-sm font-semibold mb-3 block">Color Theme</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                {[
                  { id: 'sage_cream', name: 'Sage & Cream', colors: ['#6B8E6B', '#FDFBF7', '#B48C50'] },
                  { id: 'navy_gold', name: 'Navy & Gold', colors: ['#1B2A4A', '#F8F6F0', '#C4973B'] },
                  { id: 'classic_bw', name: 'Classic B&W', colors: ['#1A1A1A', '#FFFFFF', '#555555'] },
                  { id: 'burgundy_ivory', name: 'Burgundy', colors: ['#6B2D3E', '#FFF8F0', '#8B6914'] },
                  { id: 'teal_silver', name: 'Teal & Silver', colors: ['#1A6B6B', '#F0F8F8', '#8B8B8B'] }
                ].map(theme => (
                  <button
                    key={theme.id}
                    data-testid={`theme-${theme.id}`}
                    onClick={() => setInvoiceSettings(prev => ({
                      ...prev,
                      pdfTemplate: { ...prev.pdfTemplate, colorTheme: theme.id }
                    }))}
                    className={`relative p-3 rounded-lg border-2 transition-all text-center ${
                      (invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream') === theme.id
                        ? 'border-teal-500 ring-2 ring-teal-200 dark:ring-teal-800'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex justify-center gap-1 mb-2">
                      {theme.colors.map((c, i) => (
                        <div key={i} className="w-5 h-5 rounded-full border border-gray-300" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{theme.name}</span>
                    {(invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream') === theme.id && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Border Design Selection */}
            <div>
              <Label className="text-sm font-semibold mb-3 block">Border Design</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                {[
                  { id: 'botanical', name: 'Botanical' },
                  { id: 'double_line', name: 'Double Line' },
                  { id: 'ornamental', name: 'Ornamental' },
                  { id: 'minimal', name: 'Minimal' },
                  { id: 'none', name: 'No Border' }
                ].map(border => {
                  const selectedTheme = invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream';
                  const themeColorMap = {
                    sage_cream: '#6B8E6B', navy_gold: '#1B2A4A', classic_bw: '#1A1A1A',
                    burgundy_ivory: '#6B2D3E', teal_silver: '#1A6B6B'
                  };
                  const previewColor = themeColorMap[selectedTheme] || '#6B8E6B';
                  return (
                    <button
                      key={border.id}
                      data-testid={`border-${border.id}`}
                      onClick={() => setInvoiceSettings(prev => ({
                        ...prev,
                        pdfTemplate: { ...prev.pdfTemplate, borderDesign: border.id }
                      }))}
                      className={`relative p-2 sm:p-3 rounded-lg border-2 transition-all text-center ${
                        (invoiceSettings.pdfTemplate?.borderDesign || 'botanical') === border.id
                          ? 'border-teal-500 ring-2 ring-teal-200 dark:ring-teal-800'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {/* Mini Border Preview */}
                      <div className="w-full aspect-[3/4] rounded mb-2 relative bg-white dark:bg-gray-800 overflow-hidden">
                        <BorderPreviewSVG border={border.id} previewColor={previewColor} />
                      </div>
                      <span className="text-[10px] sm:text-xs font-medium text-gray-700 dark:text-gray-300">{border.name}</span>
                      {(invoiceSettings.pdfTemplate?.borderDesign || 'botanical') === border.id && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Live Preview */}
            <div>
              <Label className="text-sm font-semibold mb-3 block">Preview</Label>
              <div className="border rounded-lg overflow-hidden flex justify-center bg-gray-100 dark:bg-gray-900 p-3 sm:p-4">
                <InvoicePreviewSVG invoiceSettings={invoiceSettings} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={onSave}
            disabled={settingsLoading}
          >
            {settingsLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Border Preview SVG Component
const BorderPreviewSVG = ({ border, previewColor }) => {
  if (border === 'botanical') {
    return (
      <svg viewBox="0 0 42 56" className="w-full h-full">
        <rect x="3" y="3" width="36" height="50" fill="none" stroke={previewColor} strokeWidth="0.7"/>
        <rect x="5" y="5" width="32" height="46" fill="none" stroke={previewColor} strokeWidth="0.25"/>
        <ellipse cx="7" cy="9" rx="2" ry="4" fill="none" stroke={previewColor} strokeWidth="0.4" opacity="0.7"/>
        <ellipse cx="35" cy="9" rx="2" ry="4" fill="none" stroke={previewColor} strokeWidth="0.4" opacity="0.7"/>
        <ellipse cx="7" cy="47" rx="2" ry="4" fill="none" stroke={previewColor} strokeWidth="0.4" opacity="0.7"/>
        <ellipse cx="35" cy="47" rx="2" ry="4" fill="none" stroke={previewColor} strokeWidth="0.4" opacity="0.7"/>
        <rect x="12" y="14" width="18" height="1.5" fill={previewColor} opacity="0.12" rx="0.3"/>
        <rect x="15" y="18" width="12" height="1" fill={previewColor} opacity="0.08" rx="0.3"/>
        <rect x="10" y="25" width="22" height="1" fill={previewColor} opacity="0.08"/>
        <rect x="10" y="28" width="22" height="1" fill={previewColor} opacity="0.06"/>
        <rect x="10" y="31" width="22" height="1" fill={previewColor} opacity="0.06"/>
      </svg>
    );
  }
  if (border === 'double_line') {
    return (
      <svg viewBox="0 0 42 56" className="w-full h-full">
        <rect x="3" y="3" width="36" height="50" fill="none" stroke={previewColor} strokeWidth="0.7"/>
        <rect x="5" y="5" width="32" height="46" fill="none" stroke={previewColor} strokeWidth="0.25"/>
        <rect x="12" y="14" width="18" height="1.5" fill={previewColor} opacity="0.12" rx="0.3"/>
        <rect x="15" y="18" width="12" height="1" fill={previewColor} opacity="0.08" rx="0.3"/>
        <rect x="10" y="25" width="22" height="1" fill={previewColor} opacity="0.08"/>
        <rect x="10" y="28" width="22" height="1" fill={previewColor} opacity="0.06"/>
      </svg>
    );
  }
  if (border === 'ornamental') {
    return (
      <svg viewBox="0 0 42 56" className="w-full h-full">
        <rect x="4" y="4" width="34" height="48" fill="none" stroke={previewColor} strokeWidth="0.4"/>
        <circle cx="6.5" cy="6.5" r="1.8" fill="none" stroke={previewColor} strokeWidth="0.4"/>
        <circle cx="35.5" cy="6.5" r="1.8" fill="none" stroke={previewColor} strokeWidth="0.4"/>
        <circle cx="6.5" cy="49.5" r="1.8" fill="none" stroke={previewColor} strokeWidth="0.4"/>
        <circle cx="35.5" cy="49.5" r="1.8" fill="none" stroke={previewColor} strokeWidth="0.4"/>
        <line x1="10" y1="4" x2="32" y2="4" stroke={previewColor} strokeWidth="0.25" strokeDasharray="1.5,0.8"/>
        <line x1="10" y1="52" x2="32" y2="52" stroke={previewColor} strokeWidth="0.25" strokeDasharray="1.5,0.8"/>
        <rect x="12" y="14" width="18" height="1.5" fill={previewColor} opacity="0.12" rx="0.3"/>
        <rect x="15" y="18" width="12" height="1" fill={previewColor} opacity="0.08" rx="0.3"/>
        <rect x="10" y="25" width="22" height="1" fill={previewColor} opacity="0.08"/>
      </svg>
    );
  }
  if (border === 'minimal') {
    return (
      <svg viewBox="0 0 42 56" className="w-full h-full">
        <rect x="4" y="4" width="34" height="48" fill="none" stroke={previewColor} strokeWidth="0.4"/>
        <rect x="12" y="14" width="18" height="1.5" fill={previewColor} opacity="0.12" rx="0.3"/>
        <rect x="15" y="18" width="12" height="1" fill={previewColor} opacity="0.08" rx="0.3"/>
        <rect x="10" y="25" width="22" height="1" fill={previewColor} opacity="0.08"/>
      </svg>
    );
  }
  // none
  return (
    <svg viewBox="0 0 42 56" className="w-full h-full">
      <rect x="12" y="14" width="18" height="1.5" fill="#aaa" opacity="0.15" rx="0.3"/>
      <rect x="15" y="18" width="12" height="1" fill="#aaa" opacity="0.1" rx="0.3"/>
      <rect x="10" y="25" width="22" height="1" fill="#aaa" opacity="0.08"/>
      <rect x="10" y="28" width="22" height="1" fill="#aaa" opacity="0.06"/>
    </svg>
  );
};

// Invoice Preview SVG Component
const InvoicePreviewSVG = ({ invoiceSettings }) => {
  const selectedTheme = invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream';
  const selectedBorder = invoiceSettings.pdfTemplate?.borderDesign || 'botanical';
  const themes = {
    sage_cream: { bg: '#FDFBF7', primary: '#6B8E6B', dark: '#365336', accent: '#B48C50', muted: '#78716C' },
    navy_gold: { bg: '#F8F6F0', primary: '#1B2A4A', dark: '#0F1A30', accent: '#C4973B', muted: '#5A6578' },
    classic_bw: { bg: '#FFFFFF', primary: '#1A1A1A', dark: '#000000', accent: '#555555', muted: '#777777' },
    burgundy_ivory: { bg: '#FFF8F0', primary: '#6B2D3E', dark: '#4A1E2B', accent: '#8B6914', muted: '#8B7D6B' },
    teal_silver: { bg: '#F0F8F8', primary: '#1A6B6B', dark: '#0E4D4D', accent: '#8B8B8B', muted: '#6B8080' }
  };
  const t = themes[selectedTheme];
  
  return (
    <div className="w-full max-w-[240px] sm:max-w-[280px]" style={{ aspectRatio: '210/297' }}>
      <svg viewBox="0 0 210 297" className="w-full h-full rounded shadow-lg" style={{ backgroundColor: t.bg }}>
        {/* Border */}
        {selectedBorder === 'botanical' && (
          <>
            <rect x="8" y="8" width="194" height="281" fill="none" stroke={t.primary} strokeWidth="0.7"/>
            <rect x="11" y="11" width="188" height="275" fill="none" stroke={t.primary} strokeWidth="0.25"/>
            <ellipse cx="15" cy="17" rx="3.5" ry="7" fill="none" stroke={t.primary} strokeWidth="0.4" opacity="0.6"/>
            <ellipse cx="195" cy="17" rx="3.5" ry="7" fill="none" stroke={t.primary} strokeWidth="0.4" opacity="0.6"/>
            <ellipse cx="15" cy="280" rx="3.5" ry="7" fill="none" stroke={t.primary} strokeWidth="0.4" opacity="0.6"/>
            <ellipse cx="195" cy="280" rx="3.5" ry="7" fill="none" stroke={t.primary} strokeWidth="0.4" opacity="0.6"/>
          </>
        )}
        {selectedBorder === 'double_line' && (
          <>
            <rect x="8" y="8" width="194" height="281" fill="none" stroke={t.primary} strokeWidth="0.7"/>
            <rect x="11" y="11" width="188" height="275" fill="none" stroke={t.primary} strokeWidth="0.25"/>
          </>
        )}
        {selectedBorder === 'ornamental' && (
          <>
            <rect x="10" y="10" width="190" height="277" fill="none" stroke={t.primary} strokeWidth="0.4"/>
            <circle cx="14" cy="14" r="3" fill="none" stroke={t.accent} strokeWidth="0.4"/>
            <circle cx="196" cy="14" r="3" fill="none" stroke={t.accent} strokeWidth="0.4"/>
            <circle cx="14" cy="283" r="3" fill="none" stroke={t.accent} strokeWidth="0.4"/>
            <circle cx="196" cy="283" r="3" fill="none" stroke={t.accent} strokeWidth="0.4"/>
            <line x1="20" y1="10" x2="190" y2="10" stroke={t.accent} strokeWidth="0.2" strokeDasharray="3,1.5"/>
            <line x1="20" y1="287" x2="190" y2="287" stroke={t.accent} strokeWidth="0.2" strokeDasharray="3,1.5"/>
          </>
        )}
        {selectedBorder === 'minimal' && (
          <rect x="10" y="10" width="190" height="277" fill="none" stroke={t.primary} strokeWidth="0.35"/>
        )}

        {/* Logo placeholder */}
        <rect x="25" y="28" width="20" height="16" fill={t.muted} opacity="0.15" rx="2"/>

        {/* Invoice title */}
        <text x="105" y="50" textAnchor="middle" fontSize="11" fill={t.dark} fontWeight="bold" fontStyle="italic">Invoice</text>
        <line x1="78" y1="54" x2="132" y2="54" stroke={t.accent} strokeWidth="0.4" opacity="0.5"/>

        {/* From / To blocks */}
        <rect x="25" y="68" width="70" height="28" fill={t.primary} opacity="0.03" rx="2"/>
        <text x="29" y="78" fontSize="3.5" fill={t.primary} fontWeight="bold">From</text>
        <rect x="29" y="81" width="50" height="1.8" fill={t.muted} opacity="0.12" rx="0.3"/>
        <rect x="29" y="85" width="38" height="1.5" fill={t.muted} opacity="0.08" rx="0.3"/>

        <rect x="115" y="68" width="70" height="28" fill={t.primary} opacity="0.03" rx="2"/>
        <text x="119" y="78" fontSize="3.5" fill={t.primary} fontWeight="bold">Bill To</text>
        <rect x="119" y="81" width="50" height="1.8" fill={t.muted} opacity="0.12" rx="0.3"/>
        <rect x="119" y="85" width="38" height="1.5" fill={t.muted} opacity="0.08" rx="0.3"/>

        {/* Table header */}
        <rect x="25" y="110" width="160" height="9" fill={t.primary} opacity="0.1" rx="1"/>
        <text x="38" y="116.5" fontSize="3.2" fill={t.dark} fontWeight="bold">Service</text>
        <text x="118" y="116.5" fontSize="3.2" fill={t.dark} fontWeight="bold">Qty</text>
        <text x="150" y="116.5" fontSize="3.2" fill={t.dark} fontWeight="bold">Amount</text>

        {/* Table rows */}
        <rect x="25" y="122" width="160" height="7" fill={t.muted} opacity="0.03"/>
        <rect x="29" y="124.5" width="55" height="1.5" fill={t.muted} opacity="0.12" rx="0.3"/>
        <rect x="118" y="124.5" width="8" height="1.5" fill={t.muted} opacity="0.1" rx="0.3"/>
        <rect x="147" y="124.5" width="22" height="1.5" fill={t.muted} opacity="0.1" rx="0.3"/>

        <rect x="25" y="132" width="160" height="7" fill="transparent"/>
        <rect x="29" y="134.5" width="45" height="1.5" fill={t.muted} opacity="0.1" rx="0.3"/>
        <rect x="118" y="134.5" width="8" height="1.5" fill={t.muted} opacity="0.08" rx="0.3"/>
        <rect x="147" y="134.5" width="22" height="1.5" fill={t.muted} opacity="0.08" rx="0.3"/>

        {/* Totals */}
        <line x1="120" y1="155" x2="185" y2="155" stroke={t.muted} strokeWidth="0.25"/>
        <text x="128" y="165" fontSize="3.2" fill={t.muted}>Subtotal</text>
        <rect x="155" y="162" width="24" height="2" fill={t.muted} opacity="0.1" rx="0.3"/>
        <text x="128" y="175" fontSize="3.2" fill={t.muted}>Tax</text>
        <rect x="155" y="172" width="24" height="2" fill={t.muted} opacity="0.1" rx="0.3"/>
        <rect x="120" y="182" width="65" height="11" fill={t.primary} opacity="0.08" rx="1"/>
        <text x="128" y="190" fontSize="4" fill={t.dark} fontWeight="bold">TOTAL</text>
        <rect x="155" y="186" width="24" height="3" fill={t.primary} opacity="0.12" rx="0.3"/>

        {/* Bank details */}
        <rect x="25" y="210" width="70" height="22" fill={t.primary} opacity="0.03" rx="2"/>
        <text x="29" y="220" fontSize="3.2" fill={t.primary} fontWeight="bold">Bank Details</text>
        <rect x="29" y="223" width="50" height="1.5" fill={t.muted} opacity="0.1" rx="0.3"/>
        <rect x="29" y="227" width="40" height="1.5" fill={t.muted} opacity="0.08" rx="0.3"/>

        {/* Footer */}
        <text x="105" y="262" textAnchor="middle" fontSize="3.5" fill={t.primary} fontStyle="italic" opacity="0.7">Thank you for your business!</text>
        <line x1="65" y1="265" x2="145" y2="265" stroke={t.accent} strokeWidth="0.2" opacity="0.4"/>
      </svg>
    </div>
  );
};

export default InvoiceSettingsDialog;
